import React, { useEffect, useRef, useState } from 'react'
import { onSaveState, retry, type SaveState } from './api'

export const RATING_LABELS: Record<number, string> = {
  1: 'Present but unusable', 2: 'Weak', 3: 'Works', 4: 'Strong', 5: 'Best in set',
}
export const PRESENCE = ['present', 'absent', 'uncaptured'] as const
export type Presence = typeof PRESENCE[number]

/** Scoring, mirrored from the server so the UI can recompute without a round trip. */
export function score(cells: { presence: Presence; rating: number | null }[]) {
  // An unrated 'present' cell is unjudged, not bad: it stays out of the base.
  const unrated = cells.filter(c => c.presence === 'present' && c.rating == null).length
  const counted = cells.filter(c =>
    c.presence === 'absent' || (c.presence === 'present' && c.rating != null))
  if (!counted.length) return { value: null as number | null, evidenced: 0, total: cells.length, unrated }
  const sum = counted.reduce((a, c) => a + (c.presence === 'present' ? (c.rating ?? 0) : 0), 0)
  return { value: Math.round((sum / counted.length) * 10) / 10, evidenced: counted.length, total: cells.length, unrated }
}

export const Score: React.FC<{ s: { value: number | null; evidenced: number; total: number; unrated?: number } }> = ({ s }) => {
  const un = s.unrated ? <span style={{ color: 'var(--warn)' }}> · {s.unrated} unrated</span> : null
  return s.value === null
    ? <><span className="na">n/a</span>
       <div className="base">{s.evidenced} of {s.total} judged{un}</div><div className="bar dash" /></>
    : <><span className="num-strong">{s.value.toFixed(1)}</span>
       <div className="base">{s.evidenced} of {s.total} judged{un}</div>
       <div className="bar"><i style={{ width: `${(s.value / 5) * 100}%` }} /></div></>
}

export const Dot: React.FC<{ p: Presence }> = ({ p }) => <i className={`dot ${p}`} />

export const PresenceCell: React.FC<{
  presence: Presence; rating: number | null; onChange: (p: Presence, r: number | null) => void
}> = ({ presence, rating, onChange }) => (
  <div style={{ display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap' }}>
    <select value={presence} style={{ width: 132 }}
      onChange={e => { const p = e.target.value as Presence; onChange(p, p === 'present' ? rating : null) }}>
      <option value="present">&#9679; present</option>
      <option value="absent">&#9675; absent</option>
      <option value="uncaptured">&#8212; uncaptured</option>
    </select>
    {presence === 'present' && (
      /* An unrated cell must never display a rating it does not have. */
      <select value={rating ?? ''} style={{ width: 158, borderColor: rating == null ? 'var(--warn-line)' : undefined }}
        onChange={e => onChange('present', e.target.value === '' ? null : Number(e.target.value))}>
        <option value="">&#8212; not rated yet</option>
        {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n} &middot; {RATING_LABELS[n]}</option>)}
      </select>
    )}
  </div>
)

/** Save indicator. Shows "saved" only on a server acknowledgement. */
export const SaveBadge: React.FC = () => {
  const [s, setS] = useState<SaveState>({ kind: 'idle' })
  useEffect(() => onSaveState(setS), [])
  if (s.kind === 'idle') return null
  if (s.kind === 'saving') return <span className="saved saving"><i className="spinner" />saving{s.pending > 1 ? ` ${s.pending}` : ''}…</span>
  if (s.kind === 'saved') return <span className="saved ok"><i className="tick">✓</i>saved {new Date(s.at).toLocaleTimeString('en-GB')}</span>
  return (
    <span className="saved err" onClick={() => retry()} title={s.message}>
      <i className="tick">!</i>not saved — {s.pending} queued · retry
    </span>
  )
}

/** A text field that commits on blur and never loses what you typed. */
export const Field: React.FC<{
  label?: string; value: string; onCommit: (v: string) => void; textarea?: boolean; mono?: boolean
  readOnly?: boolean; placeholder?: string
}> = ({ label, value, onCommit, textarea, mono, readOnly, placeholder }) => {
  const [v, setV] = useState(value)
  const dirty = useRef(false)
  useEffect(() => { if (!dirty.current) setV(value) }, [value])
  const commit = () => { dirty.current = false; if (v !== value) onCommit(v) }
  const P = { value: v, placeholder, readOnly,
    className: mono ? 'mono' : undefined,
    onChange: (e: any) => { dirty.current = true; setV(e.target.value) },
    onBlur: commit,
    onKeyDown: (e: any) => { if (e.key === 'Enter' && !textarea) (e.target as HTMLElement).blur() } }
  return <div>{label && <label className="lbl">{label}</label>}
    {textarea ? <textarea {...P} /> : <input {...P} />}</div>
}

/** A dropdown backed by a configured list, with inline "+ Add new". */
export const LookupSelect: React.FC<{
  label?: string; value: string | null; options: any[]
  onChange: (id: string | null) => void; onCreate?: (name: string) => Promise<any>
  allowNone?: boolean; required?: boolean
}> = ({ label, value, options, onChange, onCreate, allowNone = true, required }) => {
  const [adding, setAdding] = useState(false)
  const [name, setName] = useState('')
  const add = async () => {
    if (!name.trim() || !onCreate) return
    const row = await onCreate(name.trim())
    setName(''); setAdding(false); onChange(row.id)
  }
  return (
    <div>
      {label && <label className="lbl">{label}{required && ' *'}</label>}
      {adding ? (
        <div style={{ display: 'flex', gap: 5 }}>
          <input autoFocus value={name} placeholder="New name…" onChange={e => setName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') void add(); if (e.key === 'Escape') setAdding(false) }} />
          <button className="btn sm" onClick={() => void add()}>Add</button>
          <button className="btn sm ghost" onClick={() => setAdding(false)}>✕</button>
        </div>
      ) : (
        <>
          <select value={value ?? ''} onChange={e => onChange(e.target.value || null)}
            style={required && !value ? { borderColor: 'var(--err)' } : undefined}>
            {/* A select with no empty option shows its FIRST option while the
                value is null, which reads as a choice nobody made. Always give
                the null state something honest to display. */}
            {(allowNone || !value) && <option value="">{allowNone ? '— none —' : '— choose —'}</option>}
            {options.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
          {onCreate && <div style={{ marginTop: 3 }}>
            <span style={{ fontSize: 11, color: 'var(--accent)', cursor: 'pointer' }}
              onClick={() => setAdding(true)}>+ Add new</span></div>}
        </>
      )}
    </div>
  )
}

export const Panel: React.FC<{ title: string; onClose: () => void; children: React.ReactNode }> =
  ({ title, onClose, children }) => {
    useEffect(() => {
      const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
      addEventListener('keydown', h); return () => removeEventListener('keydown', h)
    }, [onClose])
    return <>
      <div className="scrim" onClick={onClose} />
      <div className="panel">
        <div className="panel-head">
          <b style={{ flex: 1, fontSize: 13.5 }}>{title}</b>
          <SaveBadge />
          <button className="ibtn" onClick={onClose}>✕</button>
        </div>
        <div style={{ padding: 16 }}>{children}</div>
      </div>
    </>
  }

export const Thumb: React.FC<{ img: any; h?: number; w?: number; onClick?: () => void; sel?: boolean }> =
  ({ img, h = 150, w, onClick, sel }) => (
    <div className={`thumb${sel ? ' sel' : ''}`} style={{ height: h, width: w ?? 'auto', aspectRatio: w ? undefined : '0.46' }}
      onClick={onClick} title={img.display_name || img.original_filename}>
      <img loading="lazy" alt={img.display_name || img.original_filename}
        src={`/${String(img.r2_key).replace(/^img\//, 'img/t/').replace(/\.[a-z0-9]+$/i, '.jpg')}`}
        onError={e => { const t = e.currentTarget; if (!t.dataset.full) { t.dataset.full = '1'; t.src = `/${img.r2_key}` } }} />
    </div>
  )

export const NotCaptured: React.FC<{ h?: number; label?: string }> = ({ h = 150, label = 'not captured' }) => (
  <div className="ghostcard" style={{ height: h, aspectRatio: '0.46' }}>{label}</div>
)

export const Empty: React.FC<{ children: React.ReactNode }> = ({ children }) =>
  <div className="card empty">{children}</div>

/** Full-size view of a screenshot. You cannot tag what you cannot read. */
export const Lightbox: React.FC<{ src: string; name?: string; onClose: () => void }> = ({ src, name, onClose }) => {
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    addEventListener('keydown', h); return () => removeEventListener('keydown', h)
  }, [onClose])
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 90, background: 'rgba(8,9,12,.86)',
      display: 'grid', placeItems: 'center', padding: 24, cursor: 'zoom-out',
    }}>
      <img src={src} alt={name ?? ''} onClick={e => e.stopPropagation()}
        style={{ maxHeight: '94vh', maxWidth: '94vw', borderRadius: 8, cursor: 'default',
          boxShadow: '0 20px 60px rgba(0,0,0,.6)' }} />
      {name && <div className="mono" style={{ position: 'fixed', bottom: 14, color: '#fff', fontSize: 11.5, opacity: .75 }}>{name}</div>}
    </div>
  )
}

/**
 * One editable configured row: rename in place, move, delete.
 * Delete states what it will do to the images attached, and is recoverable.
 */
export const ConfigRow: React.FC<{
  name: string; meta?: React.ReactNode; usage?: number; usageLabel?: string
  onRename: (v: string) => void; onDelete: () => void
  onUp?: () => void; onDown?: () => void; prefix?: React.ReactNode
}> = ({ name, meta, usage, usageLabel = 'images', onRename, onDelete, onUp, onDown, prefix }) => {
  const [confirming, setConfirming] = useState(false)
  return (
    <div style={{ borderBottom: '1px solid var(--line-2)', padding: '8px 0' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        {prefix}
        <div style={{ flex: 1, minWidth: 0 }}><Field value={name} onCommit={onRename} /></div>
        {meta}
        {usage !== undefined && <span className="chip quiet" style={{ whiteSpace: 'nowrap' }}>{usage} {usageLabel}</span>}
        <button className="ibtn" title="Move up" disabled={!onUp} onClick={onUp}
          style={{ opacity: onUp ? 1 : .3 }}>↑</button>
        <button className="ibtn" title="Move down" disabled={!onDown} onClick={onDown}
          style={{ opacity: onDown ? 1 : .3 }}>↓</button>
        <button className="ibtn" title="Delete" onClick={() => setConfirming(v => !v)}
          style={{ color: confirming ? 'var(--err)' : undefined }}>✕</button>
      </div>
      {confirming && (
        <div style={{ marginTop: 7, padding: 10, borderRadius: 7, background: 'var(--err-bg)',
          border: '1px solid var(--err-line)', fontSize: 12 }}>
          <div style={{ marginBottom: 8 }}>
            Delete <b>{name}</b>?
            {usage ? <> Its <b>{usage} {usageLabel}</b> go back to the untagged queue — they are not deleted.</> :
                     <> Nothing is attached to it.</>}
            {' '}Recoverable from the change log.
          </div>
          <button className="btn sm" style={{ borderColor: 'var(--err)', color: 'var(--err)' }}
            onClick={() => { setConfirming(false); onDelete() }}>Delete</button>
          <button className="btn sm ghost" onClick={() => setConfirming(false)}>Cancel</button>
        </div>
      )}
    </div>
  )
}
